import { useEffect, useState, useCallback } from "react";
import {
  AddDeviceForHealthProfessional,
  DeleteDeviceByID,
  GetAllDeviceHealthProfessional,
  GetDeviceById,
} from "../../../utils/apiCalls/devices";
import { useToast } from "../../../utils/ToastContext";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import useConfirmDialog from "../../../hooks/useConfirmDialog";

const DEVICE_NAME_PREFIX = "We-Be ";
// Same GS1 UDI carton prefix + (10)/(21) segment layout as the SuperAdmin
// AddDevice page and the Registration flow - kept in sync manually since all
// three send this string to the same backend field.
export const UDI_STATIC_PREFIX = "(01)00860016217508";
const UDI_LOT_CODE_RE = /^[A-Za-z0-9]{3}$/;
const UDI_SERIAL_RE = /^\d{7}$/;

const initialValues = {
  deviceName: "",
  udiLotCode: "",
  udiSerial: "",
  numberOfSlots: "",
};

const parseDeviceUdi = (deviceUdi) => {
  const match = (deviceUdi || "").match(/\(10\)([^(]*)\(21\)(.*)$/);
  return {
    udiLotCode: match?.[1] || "",
    udiSerial: match?.[2] || "",
  };
};

const useHealthCareDevices = () => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const deleteDialog = useConfirmDialog();
  const [addForm, setAddForm] = useState(initialValues);
  const [errors, setErrors] = useState(initialValues);
  const [totalRows, setTotalRows] = useState(0);
  const [fetchError, setFetchError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 500);
  const [modalOpen, setModalOpen] = useState(false);
  // Devices are immutable once added - HP users can only add, view, or
  // delete. This flag toggles the shared modal between the (editable) add
  // form and a read-only view of an existing device's details.
  const [isViewMode, setIsViewMode] = useState(false);

  const openAddModal = () => {
    setIsViewMode(false);
    setAddForm(initialValues);
    setErrors(initialValues);
    setModalOpen(true);
  };

  const openViewModal = async (row) => {
    setIsViewMode(true);
    setErrors(initialValues);
    setModalOpen(true);

    // The devices list (GetAllDeviceHealthProfessional) doesn't return
    // deviceUdi, so fetch the full record to show it.
    setIsSubmitting(true);
    try {
      const res = await GetDeviceById(row.deviceId);
      const deviceData = res?.statusCode === 200 ? res.data : row;
      setAddForm({
        deviceName: deviceData.deviceName ?? row.deviceName ?? "",
        ...parseDeviceUdi(deviceData.deviceUdi),
        numberOfSlots: deviceData.numberOfSlots ?? row.numberOfSlots ?? "",
      });
    } catch (error) {
      console.error("Error fetching device details:", error?.message);
      setAddForm({
        deviceName: row.deviceName ?? "",
        ...parseDeviceUdi(row.deviceUdi),
        numberOfSlots: row.numberOfSlots ?? "",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setAddForm(initialValues);
    setErrors(initialValues);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setAddForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateField = () => {
    const errObj = { ...initialValues };

    if (!addForm.deviceName) {
      errObj.deviceName = "This field is required";
    } else if (addForm.deviceName.length !== 4) {
      errObj.deviceName = "Enter a 4 digit code";
    }

    if (addForm.udiLotCode || addForm.udiSerial) {
      if (!UDI_LOT_CODE_RE.test(addForm.udiLotCode)) {
        errObj.udiLotCode = "Enter a 3-character alphanumeric code";
      }
      if (!UDI_SERIAL_RE.test(addForm.udiSerial)) {
        errObj.udiSerial = "Enter a 7-digit serial";
      }
    }

    setErrors(errObj);
    return Object.values(errObj).every((x) => !x);
  };

  const fetchAllDevices = useCallback(
    async (pageIndex, pageSize, searchString) => {
      setIsLoading(true);
      setFetchError("");
      const payload = {
        pageIndex,
        pageSize,
        searchString: searchString ?? "",
      };
      try {
        const response = await GetAllDeviceHealthProfessional(payload);
        if (response?.statusCode === 200) {
          setRows(response.data || []);
          setTotalRows(response.totalRecords || 0);
        } else {
          console.error("Error fetching devices:", response?.message);
          setRows([]);
          setFetchError(response?.message || "Failed to load devices");
        }
      } catch (error) {
        console.error("Error fetching devices:", error?.message);
        setRows([]);
        setFetchError(error?.message || "Failed to load devices");
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchAllDevices(page, rowsPerPage, debouncedSearchQuery);
  }, [fetchAllDevices, debouncedSearchQuery, page, rowsPerPage]);

  const retryFetchDevices = () => {
    fetchAllDevices(page, rowsPerPage, debouncedSearchQuery);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    if (!validateField()) {
      setIsSubmitting(false);
      return;
    }

    const deviceUdi =
      addForm.udiLotCode && addForm.udiSerial
        ? `${UDI_STATIC_PREFIX}(10)${addForm.udiLotCode}(21)${addForm.udiSerial}`
        : "";

    try {
      const addFormData = new FormData();
      addFormData.append("deviceName", `${DEVICE_NAME_PREFIX}${addForm.deviceName}`);
      if (deviceUdi) addFormData.append("deviceUdi", deviceUdi);
      // numberOfSlots is intentionally not sent - it's assigned by the
      // backend based on the health professional's subscription (default 10),
      // not something HP users can choose.

      const res = await AddDeviceForHealthProfessional(addFormData);
      const { statusCode, message } = res;
      if (statusCode === 200) {
        showToast("Data Added Successfully", "success");
        fetchAllDevices(page, rowsPerPage, debouncedSearchQuery);
        closeModal();
      } else {
        showToast(message || "Failed to add device", "error");
      }
    } catch (error) {
      showToast(error?.message || "Something went wrong", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (row) => deleteDialog.openDialog(row);

  const confirmDelete = async () => {
    const row = deleteDialog.target;
    setIsSubmitting(true);
    try {
      const res = await DeleteDeviceByID(row.deviceId);
      const { statusCode, message } = res;
      if (statusCode === 200) {
        showToast(message || "Device deleted successfully", "success");
        fetchAllDevices(page, rowsPerPage, searchQuery);
      } else {
        showToast(message || "Failed to delete device", "error");
      }
    } catch (error) {
      showToast(error?.message || "Failed to delete device", "error");
    } finally {
      setIsSubmitting(false);
      deleteDialog.closeDialog();
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  return {
    rows,
    page,
    rowsPerPage,
    isLoading,
    isSubmitting,
    deleteDialog,
    addForm,
    errors,
    totalRows,
    fetchError,
    searchQuery,
    modalOpen,
    isViewMode,
    openAddModal,
    openViewModal,
    closeModal,
    handleChange,
    handleSubmit,
    retryFetchDevices,
    handleDelete,
    confirmDelete,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearchChange,
  };
};

export default useHealthCareDevices;
